import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

type Variant = 'primary' | 'secondary' | 'link' | 'danger-link';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'small';
  block?: boolean;
  icon?: IconName;
  children?: ReactNode;
}

/** `button.btn` — variants map to BEM modifiers; danger-link is a link button in red. */
export function Button({
  variant = 'secondary',
  size,
  block = false,
  icon,
  className,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  const classes = [
    'btn',
    variant === 'danger-link' ? 'btn--link btn--danger-link' : `btn--${variant}`,
    size === 'small' && 'btn--small',
    block && 'btn--block',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button {...rest} type={type} className={classes}>
      {icon && <Icon name={icon} size="sm" />}
      {children}
    </button>
  );
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  icon: IconName;
  round?: boolean;
  iconSize?: 'sm' | 'xs';
}

/** `button.icon-btn` with an accessible label. */
export function IconButton({
  label,
  icon,
  round = false,
  iconSize = 'sm',
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      className={['icon-btn', round && 'icon-btn--round', className].filter(Boolean).join(' ')}
      aria-label={label}
    >
      <Icon name={icon} size={iconSize} />
    </button>
  );
}
